import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FileSignature, Loader2, CheckCircle2, XCircle, FileText, PenSquare, Download, ExternalLink, Milestone, Paperclip } from 'lucide-react';
import api, { API } from '../api';
import { useToast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';

const STATUS_LABEL = {
  fr: { upcoming: 'À venir', in_progress: 'En cours', done: 'Terminé' },
  en: { upcoming: 'Upcoming', in_progress: 'In progress', done: 'Completed' },
  zh: { upcoming: '即将进行', in_progress: '进行中', done: '已完成' },
};
const STATUS_STYLE = {
  upcoming: 'bg-gray-200 text-gray-600',
  in_progress: 'bg-amber-100 text-amber-800 border border-amber-400',
  done: 'bg-green-100 text-green-700 border border-green-400',
};

export default function ContractPage() {
  const { rfqId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { toast } = useToast();
  const { lang, t } = useLang();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [milestones, setMilestones] = useState([]);
  const [signOpen, setSignOpen] = useState(false);
  const [method, setMethod] = useState('docusign');
  const [read, setRead] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [c, m] = await Promise.all([
        api.get(`/client/contract/${rfqId}?token=${encodeURIComponent(token)}`),
        api.get(`/client/rfq/${rfqId}/milestones?token=${encodeURIComponent(token)}`).catch(() => ({ data: [] })),
      ]);
      setState({ loading: false, error: '', data: c.data });
      setMilestones(m.data || []);
      const ct = c.data?.rfq?.contact || {};
      setSignerName((p) => p || ct.name || '');
      setSignerCompany((p) => p || ct.company || '');
      setSignerEmail((p) => p || ct.email || '');
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Lien invalide ou expiré.', data: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId, token]);

  const isAccepted = state.data?.contract?.status === 'accepted';
  const isReturnFromDs = window.location.pathname.endsWith('/signature-complete');

  useEffect(() => {
    if (isReturnFromDs && !isAccepted && state.data) finalizeDs(true);
    // eslint-disable-next-line
  }, [isReturnFromDs, state.data?.contract?.id]);

  const startDs = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/client/contract/${rfqId}/docusign/start?token=${encodeURIComponent(token)}`);
      window.location.href = res.data.signingUrl;
    } catch (e) {
      toast({ title: 'DocuSign', description: e?.response?.data?.detail || 'Impossible.', duration: 15000 });
      setBusy(false);
    }
  };
  const finalizeDs = async (silent = false) => {
    try {
      const res = await api.post(`/client/contract/${rfqId}/docusign/finalize?token=${encodeURIComponent(token)}`);
      if (res.data?.status === 'accepted') { toast({ title: t('contract.signedTitle') || 'Contrat signé', description: t('contract.signedDesc') || 'Merci !', duration: 10000 }); load(); }
      else if (!silent) toast({ title: 'DocuSign', description: 'Signature en attente.', duration: 8000 });
    } catch (e) { if (!silent) toast({ title: 'DocuSign', description: e?.response?.data?.detail || 'Erreur.', duration: 8000 }); }
  };
  const acceptInternal = async () => {
    if (!read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()) {
      toast({ title: t('contract.missing') || 'Champs manquants', description: t('contract.missingDesc') || 'Merci de compléter.', duration: 8000 });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client/contract/${rfqId}/accept-internal?token=${encodeURIComponent(token)}`, {
        signerName, signerCompany, signerEmail, readConfirmed: read,
      });
      toast({ title: t('contract.acceptedTitle') || 'Contrat accepté', description: t('contract.acceptedDesc') || 'Suivi de production activé.', duration: 10000 });
      setSignOpen(false); load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible.', duration: 10000 });
    } finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-brand-yellow" /></div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-md border border-gray-200">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-brand-navy mb-2">{t('contract.errAccess') || 'Accès impossible'}</h1>
        <p className="text-gray-600 text-sm mb-6">{state.error}</p>
        <Link to="/" className="text-brand-navy hover:text-brand-yellow text-sm font-semibold">← {t('contract.back') || 'Retour'}</Link>
      </div>
    </div>
  );

  const c = state.data.contract;
  const ct = state.data.rfq?.contact || {};
  const pdfUrl = `${API}/client/contract/${rfqId}/pdf?token=${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8">
          <Link to="/" className="text-brand-yellow text-xs font-bold tracking-widest uppercase">← Yuville International</Link>
          <div className="inline-flex items-center gap-2 mt-3 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase">
            <FileSignature className="w-3 h-3" /> {t('contract.badge') || 'Étape 04 · Contrat et suivi'}
          </div>
          <h1 data-testid="contract-title" className="text-3xl md:text-4xl font-bold leading-tight">{t('contract.title') || 'Votre contrat commercial'}</h1>
          <p className="mt-3 text-white/80 max-w-2xl leading-relaxed">{t('contract.subtitle') || 'Consultez, téléchargez et signez le contrat commercial. Une fois signé, le suivi de production s\'active.'}</p>
        </div>
      </header>

      {isAccepted && (
        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-6">
          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6" data-testid="contract-accepted-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-brand-navy text-lg">{t('contract.acceptedTitle') || 'Contrat accepté'}</h2>
                <p className="text-sm text-gray-700 mt-1">
                  {t('contract.acceptedOn') || 'Signé le'} {new Date(c.acceptedAt).toLocaleString(lang === 'zh' ? 'zh-CN' : lang === 'en' ? 'en-CA' : 'fr-CA')} — {c.method === 'docusign' ? 'DocuSign' : (t('contract.internalMethod') || 'signature interne')}
                </p>
                <a href={pdfUrl} className="inline-flex items-center gap-1.5 mt-3 text-brand-navy hover:text-brand-yellow text-sm font-semibold underline" download>
                  <Download className="w-4 h-4" /> {t('contract.downloadSigned') || 'Télécharger le contrat signé'}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-brand-navy/5 border-b border-gray-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{t('contract.docLabel') || 'Contrat commercial'} · v{c.version}</div>
              <h2 className="text-brand-navy font-bold text-lg">{c.title}</h2>
              {c.manufacturerName && <div className="text-xs text-gray-500 mt-1">{t('contract.manufacturer') || 'Manufacturier'} : <b>{c.manufacturerName}</b></div>}
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-navy hover:text-brand-yellow text-sm font-semibold" data-testid="download-contract-pdf">
              <FileText className="w-4 h-4" /> {t('contract.openPdf') || 'Ouvrir en PDF'} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="p-6 md:p-10 max-h-[60vh] overflow-y-auto" data-testid="contract-content">
            <div className="prose max-w-none text-brand-navy" dangerouslySetInnerHTML={{ __html: c.content }} />
          </div>
        </div>
      </section>

      {/* Milestones timeline */}
      {isAccepted && milestones.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Milestone className="w-5 h-5 text-brand-navy" />
            <h2 className="font-bold text-brand-navy text-xl">{t('contract.trackingTitle') || 'Suivi de production'}</h2>
          </div>
          <ol className="relative border-l-2 border-brand-navy/20 ml-3 space-y-6">
            {milestones.map((m) => (
              <li key={m.id} className="pl-6 relative" data-testid={`milestone-${m.id}`}>
                <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full ring-4 ring-white ${m.status === 'done' ? 'bg-green-500' : m.status === 'in_progress' ? 'bg-amber-500' : 'bg-gray-300'}`} />
                <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="font-bold text-brand-navy">{m.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${STATUS_STYLE[m.status] || STATUS_STYLE.upcoming}`}>
                      {STATUS_LABEL[lang]?.[m.status] || m.status}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-gray-500">
                    {m.dueDate && <span>{t('contract.dueDate') || 'Échéance'} : {m.dueDate}</span>}
                    {m.completedDate && <span className="text-green-600 font-semibold">{t('contract.completedDate') || 'Complété le'} : {m.completedDate}</span>}
                  </div>
                  {m.notes && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{m.notes}</p>}
                  {m.attachments?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                      {m.attachments.map((a) => (
                        <a key={a.id} href={`${API}/client/milestones/${m.id}/attachment/${a.id}?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-brand-navy/5 text-brand-navy hover:bg-brand-navy hover:text-white transition" data-testid={`att-${a.id}`}>
                          <Paperclip className="w-3 h-3" /> {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {!isAccepted && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] z-40">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-600">{t('contract.signPrompt') || 'Prenez connaissance du contrat, puis signez pour activer le suivi.'}</div>
            <button data-testid="open-sign-contract-btn" onClick={() => setSignOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 transition">
              <PenSquare className="w-4 h-4" /> {t('contract.signBtn') || 'Signer le contrat'}
            </button>
          </div>
        </div>
      )}

      {signOpen && !isAccepted && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setSignOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold">{t('contract.signDialogTitle') || 'Signature du contrat'}</h2>
              <button disabled={busy} onClick={() => setSignOpen(false)} className="text-white/80 hover:text-white disabled:opacity-40"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600">{t('contract.chooseMethod') || 'Choisissez la méthode de signature :'}</div>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="c-method-docusign" onClick={() => setMethod('docusign')} className={`p-3 rounded-lg border text-left transition ${method === 'docusign' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{t('contract.methodDocusign') || 'Signature électronique'}</div>
                  <div className="text-[11px] text-gray-500">DocuSign</div>
                </button>
                <button data-testid="c-method-internal" onClick={() => setMethod('internal')} className={`p-3 rounded-lg border text-left transition ${method === 'internal' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{t('contract.methodInternal') || 'Acceptation en ligne'}</div>
                  <div className="text-[11px] text-gray-500">{t('contract.methodInternalHint') || 'nom + IP + horodatage'}</div>
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 pt-2">
                <input data-testid="c-signer-name" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder={t('contract.phName') || 'Nom du signataire'} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input data-testid="c-signer-company" value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} placeholder={t('contract.phCompany') || 'Entreprise'} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input data-testid="c-signer-email" type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder={t('contract.phEmail') || 'Courriel professionnel'} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                <input data-testid="c-read" type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="w-4 h-4 mt-0.5 accent-brand-yellow" />
                <span>{t('contract.readConfirm') || "J'ai lu l'intégralité du contrat et je l'accepte au nom de l'entreprise indiquée."}</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button disabled={busy} onClick={() => setSignOpen(false)} className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 text-brand-navy text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">{t('contract.cancel') || 'Annuler'}</button>
                {method === 'docusign' ? (
                  <button data-testid="c-submit-ds" disabled={busy || !read || !signerEmail} onClick={startDs} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenSquare className="w-4 h-4" />} {t('contract.submitDs') || 'Signer via DocuSign'}
                  </button>
                ) : (
                  <button data-testid="c-submit-internal" disabled={busy || !read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()} onClick={acceptInternal} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t('contract.submitInternal') || 'Accepter'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
